import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { X, Music, Calculator, Brain, TrendingUp, User, Building, AlertCircle, ExternalLink, Plus, Users, Calendar, DollarSign, Target, MapPin, ChevronsUpDown, Info, Globe, Printer, Mic2 } from "lucide-react";
import { type ShowWithDetails, type BrandWithProfiles, type InsertBrand, type InsertBrandAudienceProfile, type InsertCampaign, INDUSTRIES } from "@shared/schema";
import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MaxSpinner } from "@/components/ui/max-spinner";
import { LoadingButton } from "@/components/ui/loading-button";
import { LoadingSkeleton } from "@/components/ui/loading-skeleton";
import { apiRequest } from "@/lib/queryClient";
import QuickPersonaCreator from "@/components/quick-persona-creator";
import { useAuth } from "@/lib/auth";
import { usePricingVisibility } from "@/lib/pricing-visibility";
import { useToast } from "@/hooks/use-toast";
import { createOperationToast, showOperationToast } from "@/lib/operation-toast";
import PlanSidebar from "@/components/plan-sidebar";
import PlanToggleButton from "@/components/plan-toggle-button";
import { PlanSidebarState } from "@/lib/plan-sidebar-state";
import { useLocation, Link } from "wouter";
import { calculatePlanMetrics, getMarketingObjectiveOptions, normalizeCareerStageDisplay, formatImpressionCount, type ProposalCalculationResult } from "@/modules/calculator";
import { PlanStorage, type PackageTier } from "@/lib/plan-storage";
import { PACKAGE_CONFIGS, formatPrice } from "@/lib/package-config";
import { calculatePotentialAttendees, calculateAvgAttendeesPerShow } from "@shared/career-stage-attendance";
import { PlanMapView } from "@/components/plan-map-view";
import { PlanCalendar } from "@/components/plan-calendar";
import { PlanCalendarGrid } from "@/components/plan-calendar-grid";
import { PlanDeliverables } from "@/components/plan-deliverables";

interface PlanFormNewProps {
  selectedShows: ShowWithDetails[];
  onRemoveShow: (showId: number) => void;
  onClearShows: () => void;
}

export default function PlanFormNew({ selectedShows, onRemoveShow, onClearShows }: PlanFormNewProps) {
  const [formData, setFormData] = useState({
    brand: "",
    audienceProfile: "",
    campaign: "",
  });
  const [planName, setPlanName] = useState("");

  const [calculationResult, setCalculationResult] = useState<ProposalCalculationResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<string>("");
  const [selectedAudienceProfileId, setSelectedAudienceProfileId] = useState<number | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [showPackages, setShowPackages] = useState<Record<number, { tier: PackageTier; price?: number }>>({});
  const [isPlanSidebarOpen, setIsPlanSidebarOpen] = useState(() => PlanSidebarState.isOpen());
  
  // Modal states for inline creation
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [showAudienceModal, setShowAudienceModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [brandFormData, setBrandFormData] = useState<Partial<InsertBrand>>({});
  
  interface CampaignFormData {
    brandId?: number;
    audienceProfileId?: number;
    name?: string;
    campaignObjective?: string;
    startDate?: string;
    endDate?: string;
    description?: string;
  }
  
  const [campaignFormData, setCampaignFormData] = useState<CampaignFormData>({});
  
  const { user } = useAuth();
  const { isPricingVisible } = usePricingVisibility();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  // Fetch brands from brand management system
  const { data: brands = [], isLoading: brandsLoading } = useQuery({
    queryKey: ['/api/brands'],
    enabled: !!user,
  });

  // Fetch audience profiles for selected brand
  const { data: audienceProfiles = [], isLoading: audienceProfilesLoading } = useQuery({
    queryKey: ['/api/brands', selectedBrandId, 'audience-profiles'],
    queryFn: async () => {
      if (!selectedBrandId) return [];
      return apiRequest(`/api/brands/${selectedBrandId}/audience-profiles`);
    },
    enabled: !!user && !!selectedBrandId,
  });

  // Fetch campaigns for selected brand and audience profile
  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery({
    queryKey: ['/api/campaigns', selectedBrandId, selectedAudienceProfileId],
    queryFn: async () => {
      if (!selectedBrandId || !selectedAudienceProfileId) return [];
      const params = new URLSearchParams({
        brandId: selectedBrandId.toString(),
        audienceProfileId: selectedAudienceProfileId.toString()
      });
      return apiRequest(`/api/campaigns?${params}`);
    },
    enabled: !!user && !!selectedBrandId && !!selectedAudienceProfileId,
  });

  // Load show packages on mount and force pricing recalculation
  useEffect(() => {
    const packages = PlanStorage.getShowPackages(user?.id);
    setShowPackages(packages);
    
    // Force pricing recalculation to ensure we use current multipliers
    if (PlanStorage.getShowCount(user?.id) > 0) {
      PlanStorage.forceRecalculatePricing(user?.id);
    }
  }, [user?.id]);

  // Auto-select brand if only one available
  useEffect(() => {
    if (Array.isArray(brands) && brands.length === 1 && !selectedBrandId) {
      const brand = brands[0];
      setSelectedBrandId(brand.id);
      setSelectedBrand(brand.name);
      setFormData(prev => ({ ...prev, brand: brand.name }));
    }
  }, [brands, selectedBrandId]);

  // Auto-select audience profile if only one available
  useEffect(() => {
    if (Array.isArray(audienceProfiles) && audienceProfiles.length === 1 && !selectedAudienceProfileId && selectedBrandId) {
      const profile = audienceProfiles[0];
      setSelectedAudienceProfileId(profile.id);
      setFormData(prev => ({ ...prev, audienceProfile: profile.profileName }));
    }
  }, [audienceProfiles, selectedAudienceProfileId, selectedBrandId]);

  // Auto-select campaign if only one available
  useEffect(() => {
    if (Array.isArray(campaigns) && campaigns.length === 1 && !selectedCampaignId && selectedBrandId && selectedAudienceProfileId) {
      const campaign = campaigns[0];
      setSelectedCampaignId(campaign.id);
      setFormData(prev => ({ ...prev, campaign: campaign.name }));
    }
  }, [campaigns, selectedCampaignId, selectedBrandId, selectedAudienceProfileId]);

  // Handle package tier changes
  const handleUpdateShowPackage = (showId: number, tier: PackageTier) => {
    PlanStorage.setShowPackage([showId], tier, undefined, user?.id);
    const packages = PlanStorage.getShowPackages(user?.id);
    setShowPackages(packages);
  };

  // Get selected entities for display and validation
  const getSelectedAudienceProfile = () => {
    return Array.isArray(audienceProfiles) ? audienceProfiles.find((p: any) => p.id === selectedAudienceProfileId) : null;
  };

  const getSelectedCampaign = () => {
    return Array.isArray(campaigns) ? campaigns.find((c: any) => c.id === selectedCampaignId) : null;
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Handler functions for brand/campaign/audience selection
  const handleBrandSelection = (value: string) => {
    if (value === "__create_new__") {
      setShowBrandModal(true);
      return;
    }
    
    const brandId = parseInt(value);
    const brand = Array.isArray(brands) ? brands.find((b: any) => b.id === brandId) : null;
    
    setSelectedBrandId(brandId);
    setSelectedBrand(brand?.name || "");
    setFormData({ ...formData, brand: brand?.name || "" });
    
    // Reset dependent selections
    setSelectedAudienceProfileId(null);
    setSelectedCampaignId(null);
    setFormData({ ...formData, brand: brand?.name || "", audienceProfile: "", campaign: "" });
  };

  const handleAudienceProfileSelection = (value: string) => {
    if (value === "__create_new__") {
      setShowAudienceModal(true);
      return;
    }
    
    const profileId = parseInt(value);
    const profile = Array.isArray(audienceProfiles) ? audienceProfiles.find((p: any) => p.id === profileId) : null;
    
    setSelectedAudienceProfileId(profileId);
    setFormData({ ...formData, audienceProfile: profile?.profileName || "" });
    
    // Reset campaign selection
    setSelectedCampaignId(null);
    setFormData({ ...formData, audienceProfile: profile?.profileName || "", campaign: "" });
  };

  const handleCampaignSelection = (value: string) => {
    if (value === "__create_new__") {
      setShowCampaignModal(true);
      return;
    }
    
    const campaignId = parseInt(value);
    const campaign = Array.isArray(campaigns) ? campaigns.find((c: any) => c.id === campaignId) : null;
    
    setSelectedCampaignId(campaignId);
    setFormData({ ...formData, campaign: campaign?.name || "" });
  };

  // Helper functions
  const generateAudienceDescription = (profile: any) => {
    const parts = [];
    if (profile.ageMin && profile.ageMax) {
      parts.push(`${profile.ageMin}-${profile.ageMax} years`);
    }
    if (profile.gender && profile.gender !== 'all') {
      parts.push(profile.gender);
    }
    if (profile.interests) {
      parts.push(profile.interests.split(',').slice(0, 2).join(', '));
    }
    return parts.length > 0 ? parts.join(' • ') : 'No description';
  };

  const formatCampaignDateRange = (campaign: any) => {
    if (!campaign.startDate) return 'No dates set';
    const start = new Date(campaign.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!campaign.endDate) return `Starting ${start}`;
    const end = new Date(campaign.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${start} - ${end}`;
  };

  const calculateCareerStageBreakdown = (shows: ShowWithDetails[]) => {
    const breakdown = { emerging: 0, breakthrough: 0, established: 0, mainstream: 0 };

    shows.forEach(show => {
      const stage = (show.artist.careerStage || 'Emerging').toLowerCase();
      if (stage.includes('developing') || stage.includes('local')) {
        breakdown.emerging++; // Count Developing with Emerging
      } else if (stage.includes('emerging')) {
        breakdown.emerging++;
      } else if (stage.includes('breakthrough') || stage.includes('break-through')) {
        breakdown.breakthrough++;
      } else if (stage.includes('established')) {
        breakdown.established++;
      } else if (stage.includes('mainstream')) {
        breakdown.mainstream++;
      } else {
        breakdown.emerging++; // Default to emerging
      }
    });

    return breakdown;
  };

  const isProfileIncomplete = !user?.name || !user?.email || !user?.jobTitle || !user?.company;

  // Brand creation mutation
  const createBrandMutation = useMutation({
    mutationFn: async (brandData: Partial<InsertBrand>) => {
      return apiRequest('/api/brands', { method: 'POST', body: brandData });
    },
    onSuccess: (newBrand) => {
      queryClient.invalidateQueries({ queryKey: ['/api/brands'] });
      setSelectedBrandId(newBrand.id);
      setSelectedBrand(newBrand.name);
      setFormData({ ...formData, brand: newBrand.name });
      setShowBrandModal(false);
      setBrandFormData({});
      toast({ title: "Brand created successfully" });
    },
    onError: (error) => {
      toast({ title: "Error creating brand", variant: "destructive" });
    }
  });


  // Campaign creation mutation
  const createCampaignMutation = useMutation({
    mutationFn: async (campaignData: CampaignFormData) => {
      return apiRequest('/api/campaigns', { method: 'POST', body: campaignData });
    },
    onSuccess: (newCampaign) => {
      queryClient.invalidateQueries({ queryKey: ['/api/campaigns', selectedBrandId, selectedAudienceProfileId] });
      setSelectedCampaignId(newCampaign.id);
      setFormData({ ...formData, campaign: newCampaign.name });
      setShowCampaignModal(false);
      setCampaignFormData({});
      toast({ title: "Campaign created successfully" });
    },
    onError: (error) => {
      toast({ title: "Error creating campaign", variant: "destructive" });
    }
  });

  // Plan save mutation (simplified - no AI generation)
  const createPlanMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");

      // Get selected shows with packages
      const showsWithPackages = PlanStorage.getShowsWithPackages(user?.id);
      const selectedShows = PlanStorage.getSelectedShows(user?.id);

      // Get selected entities
      const selectedBrand = Array.isArray(brands) ? brands.find((b: any) => b.id === selectedBrandId) : null;
      const selectedAudienceProfile = getSelectedAudienceProfile();
      const selectedCampaign = getSelectedCampaign();

      // Use actual stored prices from plan builder
      const actualTotalCost = Math.round(PlanStorage.getTotalCost(user?.id) || 0);

      const planData = {
        // Basic plan info
        campaignName: planName || selectedCampaign?.name || "Untitled Plan",
        brand: selectedBrand?.name || "Brand",
        brandId: selectedBrand?.id,
        campaignGoals: selectedCampaign?.campaignObjective || "awareness",
        targetAudience: selectedAudienceProfile?.profileName || "General Audience",

        // Show and package data
        showIds: selectedShows.map(show => show.id),
        showPackages: showsWithPackages.map(sp => ({
          showId: sp.show.id,
          packageTier: sp.packageTier,
          customPrice: sp.packagePrice
        })),

        // Financial metrics
        totalBudget: actualTotalCost.toString(),
        numberOfShows: selectedShows.length,

        // Career stage breakdown (for display purposes)
        careerStageBreakdown: {
          ...calculateCareerStageBreakdown(selectedShows),
          // Store plan structure for future use
          planStructure: {
            preparerDetails: {
              name: user.name,
              email: user.email,
              jobTitle: user.jobTitle,
              company: user.company,
              companyLogoUrl: user.companyLogoUrl,
              profileImageUrl: user.profileImageUrl
            },
            brandDetails: selectedBrand ? {
              id: selectedBrand.id,
              name: selectedBrand.name,
              logoUrl: selectedBrand.logoUrl,
              industry: selectedBrand.industry,
              description: selectedBrand.description
            } : null,
            audienceProfile: selectedAudienceProfile ? {
              id: selectedAudienceProfile.id,
              profileName: selectedAudienceProfile.profileName,
              ageMin: selectedAudienceProfile.ageMin,
              ageMax: selectedAudienceProfile.ageMax,
              gender: selectedAudienceProfile.gender,
              interests: selectedAudienceProfile.interests,
              targetLocations: selectedAudienceProfile.targetLocations
            } : null,
            campaignDetails: selectedCampaign ? {
              id: selectedCampaign.id,
              name: selectedCampaign.name,
              campaignObjective: selectedCampaign.campaignObjective,
              startDate: selectedCampaign.startDate,
              endDate: selectedCampaign.endDate,
              budget: selectedCampaign.budget
            } : null
          }
        }
      };

      const response = await apiRequest("/api/plans", { method: "POST", body: planData });
      return response;
    },
    onSuccess: async (plan) => {
      queryClient.invalidateQueries({ queryKey: ["/api/plans"] });

      toast({
        title: "Plan saved successfully",
        description: "Your plan has been saved and can be accessed from Manage Plans.",
      });

      // Keep the plan in the builder - don't clear or redirect
    },
    onError: (error) => {
      toast({
        title: "Error saving plan",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (PlanStorage.getShowCount(user?.id) === 0) {
      toast({
        title: "No shows selected",
        description: "Please select at least one show for your plan.",
        variant: "destructive",
      });
      return;
    }

    createPlanMutation.mutate();
  };

  // Helper to convert showPackages format for new components
  const convertShowPackagesFormat = () => {
    return Object.entries(showPackages).reduce((acc, [showId, pkg]) => {
      acc[Number(showId)] = {
        showId: Number(showId),
        setCore: pkg.tier === 'set-core',
        setAudio: pkg.tier === 'set-audio',
        setVideo: pkg.tier === 'set-video'
      };
      return acc;
    }, {} as Record<number, { showId: number, setCore: boolean, setAudio: boolean, setVideo: boolean }>);
  };

  return (
    <>
    <div className="container mx-auto px-4 py-8 max-w-6xl space-y-8">
      {/* Top Section - Who's Preparing and Plan Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Who's Preparing */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Who's Preparing This Plan?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
              <div className="flex items-center justify-between">
                {/* Personal Information - Left Side */}
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={user?.profileImageUrl} alt={user?.name} />
                    <AvatarFallback className="text-sm">
                      {user?.name ? getInitials(user.name) : <User className="w-4 h-4" />}
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">{user?.name || "Not provided"}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300">{user?.jobTitle || "Not provided"}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300">{user?.email || "Not provided"}</p>
                  </div>
                </div>
                
                {/* Company Information - Right Side */}
                <div className="flex items-center gap-3">
                  <div className="text-right space-y-0.5">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">{user?.company || "Not provided"}</p>
                  </div>
                  {user?.companyLogoUrl && (
                    <div className="w-10 h-10 flex items-center justify-center bg-white rounded border">
                      <img src={user.companyLogoUrl} alt="Company logo" className="max-w-8 max-h-8 object-contain" />
                    </div>
                  )}
                </div>
              </div>
              
              {isProfileIncomplete && (
                <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded mt-3">
                  <AlertCircle className="w-3 h-3 text-amber-600" />
                  <p className="text-xs text-amber-800">
                    Complete your profile for better plans.
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <a href="/profile" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-2 h-2 mr-1" />
                      Edit
                    </a>
                  </Button>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              {PlanStorage.getShowCount(user?.id) === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-4">
                  Add shows to create your plan
                </div>
              ) : (
                <div className="flex gap-3">
                  <LoadingButton
                    variant="default"
                    className="flex-1"
                    onClick={() => handleSubmit()}
                    loading={createPlanMutation.isPending}
                    loadingText="Saving Plan..."
                  >
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Save Plan
                  </LoadingButton>
                  <Button
                    variant="outline"
                    className="flex-1 no-print"
                    onClick={() => window.print()}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Plan Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Plan Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Plan Name Section */}
            <div>
              <Label htmlFor="plan-name">Plan Name</Label>
              <Input
                id="plan-name"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder="e.g., Summer 2025 Tour Sponsorship"
                className="mt-1"
              />
            </div>

            {/* Brand Section */}
            <div>
              <Label>Brand</Label>
              <Select value={selectedBrandId?.toString() || ""} onValueChange={handleBrandSelection}>
                <SelectTrigger>
                  <SelectValue placeholder="Select brand..." />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(brands) && brands.map((brand: any) => (
                    <SelectItem key={brand.id} value={brand.id.toString()}>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4" />
                          <span>{brand.name}</span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem value="__create_new__" className="text-blue-600">
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      <span>Create new brand</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Audience Profile Section */}
            <div>
              <Label>Audience Persona</Label>
              <Select 
                value={selectedAudienceProfileId?.toString() || ""} 
                onValueChange={handleAudienceProfileSelection}
                disabled={!selectedBrandId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={selectedBrandId ? "Select persona..." : "Select brand first"} />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(audienceProfiles) && audienceProfiles.map((profile: any) => (
                    <SelectItem key={profile.id} value={profile.id.toString()}>
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        <span>{profile.profileName}</span>
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem value="__create_new__" className="text-blue-600" disabled={!selectedBrandId}>
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      <span>Create new persona</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Campaign Section */}
            <div>
              <Label>Campaign</Label>
              <Select 
                value={selectedCampaignId?.toString() || ""} 
                onValueChange={handleCampaignSelection}
                disabled={!selectedBrandId || !selectedAudienceProfileId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !selectedBrandId ? "Select brand first" :
                    !selectedAudienceProfileId ? "Select persona first" :
                    "Select campaign..."
                  } />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(campaigns) && campaigns.map((campaign: any) => (
                    <SelectItem key={campaign.id} value={campaign.id.toString()}>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>{campaign.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem value="__create_new__" className="text-blue-600" disabled={!selectedBrandId || !selectedAudienceProfileId}>
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      <span>Create new campaign</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Campaign Details - Show additional info when campaign is selected */}
            {getSelectedCampaign() && (
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Campaign Details</h4>
                
                {/* Campaign Duration */}
                {getSelectedCampaign()?.startDate && (
                  <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">Duration</span>
                    </div>
                    <div className="text-sm font-bold text-blue-700">
                      {formatCampaignDateRange(getSelectedCampaign())}
                    </div>
                  </div>
                )}

                {/* Geographic Reach - only show if shows are selected */}
                {PlanStorage.getShowCount(user?.id) > 0 && (
                  <div className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-900">Geographic Reach</span>
                    </div>
                    <div className="text-sm font-bold text-green-700">
                      {[...new Set(PlanStorage.getSelectedShows(user?.id).map(show => show.venue.market?.name || 'Unknown'))].filter(m => m !== 'Unknown').length} markets
                    </div>
                  </div>
                )}

                {/* Campaign Objective */}
                {getSelectedCampaign()?.campaignObjective && (
                  <div className="flex items-center justify-between bg-purple-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-900">Objective</span>
                    </div>
                    <div className="text-sm font-bold text-purple-700 capitalize">
                      {getSelectedCampaign()?.campaignObjective}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dashboard Layout - Overview, Timeline, and Deliverables */}
      {PlanStorage.getShowCount(user?.id) > 0 && (() => {
        // Check if any show has a custom package
        const showsWithPackages = PlanStorage.getShowsWithPackages(user?.id);
        const hasCustomPackage = showsWithPackages.some(sp => sp.packageTier === 'custom');

        return (
        <div className="space-y-6">
          {/* Overview Section - Plan Summary + Map View */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Plan Summary - 1/3 width */}
            <Card className="plan-summary-card border-pink-200/50 overflow-hidden flex flex-col h-full shadow-lg">
              {/* Hero Price Section with Gradient */}
              <div className="bg-gradient-to-br from-[#FA017C] to-[#0240FB] p-6 text-white relative overflow-hidden">
                {/* Subtle animated background overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent animate-pulse opacity-50"></div>
                <div className="relative z-10">
                  {isPricingVisible ? (
                    hasCustomPackage ? (
                      <div className="text-center">
                        <p className="text-sm font-medium opacity-90 mb-2">Total Investment</p>
                        <p className="text-3xl font-bold">Contact for Pricing</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-sm font-medium opacity-90">Total Investment</p>
                        </div>
                        <p className="text-5xl font-bold mb-3">
                          {formatPrice(PlanStorage.getTotalCost(user?.id))}
                        </p>
                        <div className="pt-3 border-t border-white/20">
                          <p className="text-lg font-medium">
                            {formatPrice(Math.round(PlanStorage.getTotalCost(user?.id) / Math.max(PlanStorage.getShowCount(user?.id), 1)))} <span className="text-sm opacity-80">avg per show</span>
                          </p>
                        </div>
                      </>
                    )
                  ) : (
                    <div className="text-center">
                      <p className="text-sm font-medium opacity-90 mb-2">Plan Summary</p>
                      <p className="text-2xl font-bold">Contact for</p>
                      <p className="text-2xl font-bold">Pricing</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Stat Grid */}
              <CardContent className="p-6 space-y-3 flex-1">
                {/* Row 1: Shows, Artists, Markets, States */}
                <div className="plan-summary-stats grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* Shows Card */}
                  <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-blue-500 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative z-10 text-center">
                      <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-blue-200 group-hover:scale-110 transition-all duration-300 mx-auto">
                        <Mic2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <p className="text-2xl font-bold text-blue-900">{PlanStorage.getShowCount(user?.id)}</p>
                      <p className="text-xs text-blue-600 font-medium">Shows</p>
                    </div>
                  </div>

                  {/* Artists Card */}
                  <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-pink-500 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative z-10 text-center">
                      <div className="w-9 h-9 bg-pink-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-pink-200 group-hover:scale-110 transition-all duration-300 mx-auto">
                        <Music className="h-5 w-5 text-pink-600" />
                      </div>
                      <p className="text-2xl font-bold text-pink-900">
                        {[...new Set(PlanStorage.getSelectedShows(user?.id).map(show => show.artist.id))].length}
                      </p>
                      <p className="text-xs text-pink-600 font-medium">Artists</p>
                    </div>
                  </div>

                  {/* Markets Card */}
                  {(() => {
                    const selectedShows = PlanStorage.getSelectedShows(user?.id);
                    const uniqueMarkets = [...new Set(
                      selectedShows
                        .map(show => show.venue.market?.name || null)
                        .filter(Boolean)
                    )].length;

                    return (
                      <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-purple-500 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative z-10 text-center">
                          <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-purple-200 group-hover:scale-110 transition-all duration-300 mx-auto">
                            <MapPin className="h-5 w-5 text-purple-600" />
                          </div>
                          <p className="text-2xl font-bold text-purple-900">{uniqueMarkets}</p>
                          <p className="text-xs text-purple-600 font-medium">{uniqueMarkets === 1 ? 'Market' : 'Markets'}</p>
                        </div>
                      </div>
                    );
                  })()}

                  {/* States Card */}
                  {(() => {
                    const selectedShows = PlanStorage.getSelectedShows(user?.id);
                    const uniqueStates = [...new Set(
                      selectedShows
                        .map(show => show.venue.state || null)
                        .filter(Boolean)
                    )].length;

                    return (
                      <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-cyan-500 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative z-10 text-center">
                          <div className="w-9 h-9 bg-cyan-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-cyan-200 group-hover:scale-110 transition-all duration-300 mx-auto">
                            <Globe className="h-5 w-5 text-cyan-600" />
                          </div>
                          <p className="text-2xl font-bold text-cyan-900">{uniqueStates}</p>
                          <p className="text-xs text-cyan-600 font-medium">{uniqueStates === 1 ? 'State' : 'States'}</p>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Row 2: Duration Card (Full Width) */}
                {(() => {
                  const selectedShows = PlanStorage.getSelectedShows(user?.id);
                  if (selectedShows.length === 0) {
                    return (
                      <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-l-4 border-amber-500 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative z-10 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 group-hover:scale-110 transition-all duration-300">
                              <Calendar className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-amber-900">0</p>
                              <p className="text-xs text-amber-600 font-medium">Days</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  const sortedShows = [...selectedShows].sort((a, b) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime()
                  );
                  const firstDate = new Date(sortedShows[0].date);
                  const lastDate = new Date(sortedShows[sortedShows.length - 1].date);
                  const days = Math.ceil((lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));

                  // Format individual dates with fixed width (3-char month, 2-digit day, 4-digit year)
                  const formatDate = (date: Date) => {
                    const month = date.toLocaleDateString('en-US', { month: 'short' });
                    const day = date.getDate().toString().padStart(2, '0');
                    const year = date.getFullYear();
                    return `${month} ${day}, ${year}`;
                  };

                  return (
                    <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-l-4 border-amber-500 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative z-10 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 group-hover:scale-110 transition-all duration-300">
                            <Calendar className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-amber-900">{days}</p>
                            <p className="text-xs text-amber-600 font-medium">{days === 1 ? 'Day' : 'Days'}</p>
                          </div>
                        </div>
                        <div className="text-right space-y-0.5">
                          <p className="text-xs font-medium text-amber-900">
                            <span className="text-amber-600">Start:</span> {formatDate(firstDate)}
                          </p>
                          <p className="text-xs font-medium text-amber-900">
                            <span className="text-amber-600">End:</span> {formatDate(lastDate)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Row 3: Potential Live Attendees Card (Full Width) */}
                {(() => {
                  const selectedShows = PlanStorage.getSelectedShows(user?.id);
                  const totalAttendees = calculatePotentialAttendees(selectedShows);
                  const avgAttendees = calculateAvgAttendeesPerShow(selectedShows);

                  return (
                    <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-l-4 border-green-500 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative z-10 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 group-hover:scale-110 transition-all duration-300">
                            <Users className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-900">{totalAttendees.toLocaleString()}</p>
                            <p className="text-xs text-green-600 font-medium whitespace-nowrap">Live Attendees</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-900">{avgAttendees.toLocaleString()}</p>
                          <p className="text-xs text-green-600 font-medium whitespace-nowrap">avg per show</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Map View - 2/3 width */}
            <div className="plan-map-view lg:col-span-2 flex flex-col h-full">
              <PlanMapView
                shows={PlanStorage.getSelectedShows(user?.id)}
                showPackages={convertShowPackagesFormat()}
              />
            </div>
          </div>

          {/* Timeline Section - Full Width */}
          <PlanCalendar
            shows={PlanStorage.getSelectedShows(user?.id)}
            showPackages={convertShowPackagesFormat()}
            campaignStart={getSelectedCampaign()?.startDate || undefined}
            campaignEnd={getSelectedCampaign()?.endDate || undefined}
          />

          {/* Calendar Grid Section - Full Width */}
          <PlanCalendarGrid
            shows={PlanStorage.getSelectedShows(user?.id)}
            showPackages={convertShowPackagesFormat()}
            campaignStart={getSelectedCampaign()?.startDate || undefined}
            campaignEnd={getSelectedCampaign()?.endDate || undefined}
          />

          {/* Deliverables Section - Full Width */}
          <PlanDeliverables
            showPackages={convertShowPackagesFormat()}
          />
        </div>
      );
    })()}
  </div>

      {/* Plan Sidebar */}
      <PlanSidebar 
        isOpen={isPlanSidebarOpen}
        onToggle={() => {
          const newState = !isPlanSidebarOpen;
          setIsPlanSidebarOpen(newState);
          PlanSidebarState.setOpen(newState);
        }}
      />
      
      {/* Plan Toggle Button */}
      <PlanToggleButton 
        onClick={() => {
          setIsPlanSidebarOpen(true);
          PlanSidebarState.setOpen(true);
        }}
      />

      {/* Modal Dialogs for Creating New Entities */}
      
      {/* Brand Creation Modal */}
      <Dialog open={showBrandModal} onOpenChange={setShowBrandModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Brand</DialogTitle>
            <DialogDescription>
              Add a new brand to your account for sponsorship planning.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="brand-name">Brand Name *</Label>
              <Input
                id="brand-name"
                value={brandFormData.name || ""}
                onChange={(e) => setBrandFormData({ ...brandFormData, name: e.target.value })}
                placeholder="Enter brand name"
              />
            </div>
            <div>
              <Label htmlFor="brand-industry">Industry</Label>
              <Select
                value={brandFormData.industry?.[0] || ""}
                onValueChange={(value) => setBrandFormData({ ...brandFormData, industry: [value] })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((industry) => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="brand-description">Description</Label>
              <Textarea
                id="brand-description"
                value={brandFormData.description || ""}
                onChange={(e) => setBrandFormData({ ...brandFormData, description: e.target.value })}
                placeholder="Brief brand description"
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowBrandModal(false)}>
                Cancel
              </Button>
              <LoadingButton
                onClick={() => createBrandMutation.mutate({ ...brandFormData, userId: user?.id })}
                loading={createBrandMutation.isPending}
                disabled={!brandFormData.name}
              >
                Create Brand
              </LoadingButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audience Profile Creation Modal */}
      {showAudienceModal && (
        <QuickPersonaCreator
          preSelectedBrandId={selectedBrandId ?? undefined}
          onClose={() => setShowAudienceModal(false)}
          onPersonaCreated={(persona) => {
            // Refresh audience profiles data
            queryClient.invalidateQueries({ queryKey: ['/api/brands', selectedBrandId, 'audience-profiles'] });
            setShowAudienceModal(false);
            // Auto-select the newly created persona
            setSelectedAudienceProfileId(persona.id);
            setFormData({ ...formData, audienceProfile: persona.profileName });
          }}
        />
      )}

      {/* Campaign Creation Modal */}
      <Dialog open={showCampaignModal} onOpenChange={setShowCampaignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Campaign</DialogTitle>
            <DialogDescription>
              Set up a new marketing campaign for this brand and audience.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="campaign-name">Campaign Name *</Label>
              <Input
                id="campaign-name"
                value={campaignFormData.name || ""}
                onChange={(e) => setCampaignFormData({ ...campaignFormData, name: e.target.value })}
                placeholder="e.g., Summer Music Festival Tour"
              />
            </div>
            <div>
              <Label htmlFor="campaign-objective">Campaign Objective</Label>
              <Select
                value={campaignFormData.campaignObjective || ""}
                onValueChange={(value) => setCampaignFormData({ ...campaignFormData, campaignObjective: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select objective" />
                </SelectTrigger>
                <SelectContent>
                  {getMarketingObjectiveOptions().map((objective) => (
                    <SelectItem key={objective.value} value={objective.value}>
                      {objective.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={campaignFormData.startDate || ""}
                  onChange={(e) => setCampaignFormData({ ...campaignFormData, startDate: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={campaignFormData.endDate || ""}
                  onChange={(e) => setCampaignFormData({ ...campaignFormData, endDate: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="campaign-description">Description</Label>
              <Textarea
                id="campaign-description"
                value={campaignFormData.description || ""}
                onChange={(e) => setCampaignFormData({ ...campaignFormData, description: e.target.value })}
                placeholder="Campaign goals and strategy"
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCampaignModal(false)}>
                Cancel
              </Button>
              <LoadingButton
                onClick={() => createCampaignMutation.mutate({
                  ...campaignFormData,
                  brandId: selectedBrandId ?? undefined,
                  audienceProfileId: selectedAudienceProfileId ?? undefined
                })}
                loading={createCampaignMutation.isPending}
                disabled={!campaignFormData.name}
              >
                Create Campaign
              </LoadingButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}